import { CustomMaterialWrapper } from './CustomMaterialWrapper.js';
import { ShaderMaterial as BABYLON_ShaderMaterial } from '@inter-ikea-betrakta-babylonjs/core/Materials/shaderMaterial.js';
import { Constants as BABYLON_Constants } from '@inter-ikea-betrakta-babylonjs/core/Engines/constants.js';
import { Vector3 as BABYLON_Vector3, Vector4 as BABYLON_Vector4 } from '@inter-ikea-betrakta-babylonjs/core/Maths/math.vector.js';
import { Material as BABYLON_Material } from '@inter-ikea-betrakta-babylonjs/core/Materials/material.js';
import { ALBEDO_ALPHA_INSTANCE_ATTRIBUTE_NAME } from './ViewerUiMeshMaterial.js';
import { RawTexture as BABYLON_RawTexture } from '@inter-ikea-betrakta-babylonjs/core/Materials/Textures/rawTexture.js';
import '../shaders/betrakta_pbr.vertex.js';
import '../shaders/betrakta_pbr.fragment.js';
import { SpotLight } from '../light-manager/light-system/lights/SpotLight.js';
import { IColor, IVector2, IVector3 } from '../../api.js';
import { uuidv4 } from '../../uuidv4.js';
import { Vector3ConstantReference } from './PropertyWrappers.js';
export class BetraktaPBRMaterialImplementation extends BABYLON_ShaderMaterial {
    static initialize_default_textures(viewer) {
        if (BetraktaPBRMaterialImplementation._has_initialized_default_textures)
            return;
        BetraktaPBRMaterialImplementation._has_initialized_default_textures = true;
        BetraktaPBRMaterialImplementation._default_albedo_texture = new BABYLON_RawTexture(new Uint8Array([255, 255, 255, 255]), 1, 1, BABYLON_Constants.TEXTUREFORMAT_RGBA, viewer.scene, false, false, BABYLON_Constants.TEXTURE_NEAREST_NEAREST, BABYLON_Constants.TEXTURETYPE_UNSIGNED_BYTE);
        BetraktaPBRMaterialImplementation._default_normal_texture = new BABYLON_RawTexture(new Uint8Array([127, 127, 255, 255]), 1, 1, BABYLON_Constants.TEXTUREFORMAT_RGBA, viewer.scene, false, false, BABYLON_Constants.TEXTURE_NEAREST_NEAREST, BABYLON_Constants.TEXTURETYPE_UNSIGNED_BYTE);
        viewer.get_viewer_dispose_observable().add_once(() => {
            var _a, _b;
            (_a = BetraktaPBRMaterialImplementation._default_albedo_texture) === null || _a === void 0 ? void 0 : _a.dispose();
            (_b = BetraktaPBRMaterialImplementation._default_normal_texture) === null || _b === void 0 ? void 0 : _b.dispose();
            BetraktaPBRMaterialImplementation._default_albedo_texture = null;
            BetraktaPBRMaterialImplementation._default_normal_texture = null;
            BetraktaPBRMaterialImplementation._has_initialized_default_textures = false;
        });
    }
    constructor(viewer, wrapper) {
        super('Betrakta PBR Material', viewer.scene, 'betrakta_pbr', {
            attributes: ['position', 'uv', 'normal', 'tangent', ALBEDO_ALPHA_INSTANCE_ATTRIBUTE_NAME],
            uniforms: [
                'viewProjection',
                'diffuseColorFactor',
                'exposure',
                'alphaCutoff',
                'uvTransform0', 'uvTransform1',
                'lightDirection', 'lightPosition', 'lightColor', 'lightValues',
                'environmentMapIntensity',
                'diffuseSH_L00',
                'diffuseSH_L1_1',
                'diffuseSH_L10',
                'diffuseSH_L11',
                'diffuseSH_L2_2',
                'diffuseSH_L2_1',
                'diffuseSH_L20',
                'diffuseSH_L21',
                'diffuseSH_L22'
            ],
            samplers: [
                'diffuseTexture',
                'normalTexture',
                'environmentMap'
            ],
            needAlphaBlending: false,
            needAlphaTesting: true
        });
        this.base_color_texture = null;
        this.normal_texture = null;
        // TODO: Implement
        this.occlusion_roughness_metallic_texture = null;
        // TODO: Implement
        this.emissive_texture = null;
        this.base_color_factor = new IVector3(1, 1, 1);
        // TODO: Implement
        this.emissive_factor = new IVector3(0, 0, 0);
        this.emissive_strength = 1.0;
        // TODO: Implement
        this.unlit = false;
        this.roughness_factor = 1.0;
        this.metallic_factor = 0.0;
        this.alpha_factor = 1.0;
        this._viewer = viewer;
        this.wrapper = wrapper;
        BetraktaPBRMaterialImplementation.initialize_default_textures(viewer);
    }
    load_babylon_pbr_material(src) {
        this.base_color_texture = src.albedoTexture;
        this.normal_texture = src.bumpTexture;
        this.occlusion_roughness_metallic_texture = src.metallicTexture;
        this.emissive_texture = src.emissiveTexture;
        if (src.albedoTexture !== null) {
            const matrix = src.albedoTexture.getTextureMatrix();
            this.setVector3('uvTransform0', new BABYLON_Vector3(matrix.m[0], matrix.m[4], matrix.m[8]));
            this.setVector3('uvTransform1', new BABYLON_Vector3(matrix.m[1], matrix.m[5], matrix.m[9]));
        }
        this.setVector4('diffuseColorFactor', new BABYLON_Vector4(src.albedoColor.r, src.albedoColor.g, src.albedoColor.b, src.alpha));
        this.alphaMode = src.alphaMode;
        this.transparencyMode = src.transparencyMode;
        this.depthFunction = src.depthFunction;
        this.backFaceCulling = src.backFaceCulling;
        this.cullBackFaces = src.cullBackFaces;
        this.sideOrientation = src.sideOrientation;
        this.setFloat('alphaCutoff', src.needAlphaTesting() ? src.alphaCutOff : -1.0);
        this.options.needAlphaBlending = this.transparencyMode === BABYLON_Material.MATERIAL_ALPHABLEND || this.transparencyMode === BABYLON_Material.MATERIAL_ALPHATESTANDBLEND;
    }
    // eslint-disable-next-line @typescript-eslint/naming-convention
    _afterBind(mesh, effect = null) {
        var _a, _b, _c, _d;
        super._afterBind(mesh, effect);
        effect === null || effect === void 0 ? void 0 : effect.setFloat('exposure', this._viewer.scene.imageProcessingConfiguration.exposure);
        effect === null || effect === void 0 ? void 0 : effect.setMatrix('viewProjection', this.getScene().getTransformMatrix());
        const lights = this._viewer.light_system.get_all_lights();
        let light = null;
        for (let i = 0; i < lights.length; i++) {
            if (lights[i] instanceof SpotLight) {
                // @ts-ignore
                light = lights[i];
                break;
            }
        }
        if (light !== null) {
            effect === null || effect === void 0 ? void 0 : effect.setVector3('lightDirection', light._direction);
            effect === null || effect === void 0 ? void 0 : effect.setVector3('lightPosition', light.get_world_position());
            effect === null || effect === void 0 ? void 0 : effect.setVector3('lightColor', IVector3.scale(IColor.to_linear_space(light.color), light.intensity));
            effect === null || effect === void 0 ? void 0 : effect.setVector2('lightValues', new IVector2(Math.cos(light.outer_angle_radians * 0.5), Math.cos(light.inner_angle_radians * 0.5)));
        }
        effect === null || effect === void 0 ? void 0 : effect.setTexture('environmentMap', this._viewer.scene.environmentTexture);
        effect === null || effect === void 0 ? void 0 : effect.setFloat('environmentMapIntensity', this._viewer.scene.environmentIntensity);
        effect === null || effect === void 0 ? void 0 : effect.setTexture('diffuseTexture', (_a = this.base_color_texture) !== null && _a !== void 0 ? _a : BetraktaPBRMaterialImplementation._default_albedo_texture);
        effect === null || effect === void 0 ? void 0 : effect.setTexture('normalTexture', (_b = this.normal_texture) !== null && _b !== void 0 ? _b : BetraktaPBRMaterialImplementation._default_normal_texture);
        const normalized_diffuse_spherical_harmonics = (_d = (_c = this._viewer.scene.environmentTexture) === null || _c === void 0 ? void 0 : _c.sphericalPolynomial) === null || _d === void 0 ? void 0 : _d.preScaledHarmonics;
        if (normalized_diffuse_spherical_harmonics) {
            effect === null || effect === void 0 ? void 0 : effect.setVector3('diffuseSH_L00', normalized_diffuse_spherical_harmonics.l00);
            effect === null || effect === void 0 ? void 0 : effect.setVector3('diffuseSH_L1_1', normalized_diffuse_spherical_harmonics.l1_1);
            effect === null || effect === void 0 ? void 0 : effect.setVector3('diffuseSH_L10', normalized_diffuse_spherical_harmonics.l10);
            effect === null || effect === void 0 ? void 0 : effect.setVector3('diffuseSH_L11', normalized_diffuse_spherical_harmonics.l11);
            effect === null || effect === void 0 ? void 0 : effect.setVector3('diffuseSH_L2_2', normalized_diffuse_spherical_harmonics.l2_2);
            effect === null || effect === void 0 ? void 0 : effect.setVector3('diffuseSH_L2_1', normalized_diffuse_spherical_harmonics.l2_1);
            effect === null || effect === void 0 ? void 0 : effect.setVector3('diffuseSH_L20', normalized_diffuse_spherical_harmonics.l20);
            effect === null || effect === void 0 ? void 0 : effect.setVector3('diffuseSH_L21', normalized_diffuse_spherical_harmonics.l21);
            effect === null || effect === void 0 ? void 0 : effect.setVector3('diffuseSH_L22', normalized_diffuse_spherical_harmonics.l22);
        }
    }
}
BetraktaPBRMaterialImplementation._default_albedo_texture = null;
BetraktaPBRMaterialImplementation._default_normal_texture = null;
BetraktaPBRMaterialImplementation._has_initialized_default_textures = false;
export class BetraktaPBRMaterial extends CustomMaterialWrapper {
    constructor(viewer, base) {
        super("BetraktaPBRMaterial");
        // TODO
        this.reflection_texture = null;
        this.lightmap_texture = null;
        this.babylon_material = new BetraktaPBRMaterialImplementation(viewer, this);
        if (base !== null) {
            this.babylon_material.load_babylon_pbr_material(base);
        }
        this._base_color_factor = new Vector3ConstantReference(this.babylon_material, 'base_color_factor');
        this._emissive_factor = new Vector3ConstantReference(this.babylon_material, 'emissive_factor');
        this._texture_manager = viewer.asset_manager.textures;
        this._viewer = viewer;
        this.id = '[BPBR] ' + uuidv4();
    }
    get_texture_id(texture) {
        if (texture === null)
            return null;
        const found_id = this._texture_manager.find_id(texture);
        if (found_id === -1) {
            return null;
        }
        else {
            return found_id;
        }
    }
    clone(name_remapper) {
        const new_name = name_remapper ? name_remapper(this.babylon_material.name) : ("Clone of " + this.babylon_material.name);
        const clone = new BetraktaPBRMaterial(this._viewer, null);
        clone.babylon_material.name = new_name;
        // Copy textures inside the material, because we don't necessarily have the textures in our texture manager. Fix?
        clone.babylon_material.base_color_texture = this.babylon_material.base_color_texture;
        clone.babylon_material.normal_texture = this.babylon_material.normal_texture;
        clone.babylon_material.occlusion_roughness_metallic_texture = this.babylon_material.occlusion_roughness_metallic_texture;
        clone.babylon_material.emissive_texture = this.babylon_material.emissive_texture;
        // TODO: Fix these
        clone.reflection_texture = this.reflection_texture;
        clone.lightmap_texture = this.lightmap_texture;
        clone.base_color_factor = this.base_color_factor;
        clone.unlit = this.unlit;
        clone.metallic = this.metallic;
        clone.roughness = this.roughness;
        clone.opacity = this.opacity;
        clone.emissive_factor = this.emissive_factor;
        clone.emissive_strength = this.emissive_strength;
        clone.babylon_material.sideOrientation = this.babylon_material.sideOrientation;
        return clone;
    }
    dispose() {
        this.babylon_material.dispose();
    }
    get base_color_factor() {
        return this._base_color_factor;
    }
    set base_color_factor(factor) {
        IVector3.copy_to_ref(factor, this._base_color_factor);
    }
    get base_color_texture() {
        return this.get_texture_id(this.babylon_material.base_color_texture);
    }
    set base_color_texture(texture) {
        if (texture === null) {
            this.babylon_material.base_color_texture = null;
        }
        else {
            this.babylon_material.base_color_texture = this._texture_manager.get(texture);
        }
    }
    get normal_texture() {
        return this.get_texture_id(this.babylon_material.normal_texture);
    }
    set normal_texture(texture) {
        if (texture === null) {
            this.babylon_material.normal_texture = null;
        }
        else {
            this.babylon_material.normal_texture = this._texture_manager.get(texture);
        }
    }
    get occlusion_roughness_metallic_texture() {
        return this.get_texture_id(this.babylon_material.occlusion_roughness_metallic_texture);
    }
    set occlusion_roughness_metallic_texture(texture) {
        if (texture === null) {
            this.babylon_material.occlusion_roughness_metallic_texture = null;
        }
        else {
            this.babylon_material.occlusion_roughness_metallic_texture = this._texture_manager.get(texture);
        }
    }
    get unlit() {
        return this.babylon_material.unlit;
    }
    set unlit(unlit) {
        this.babylon_material.unlit = unlit;
    }
    get metallic() {
        return this.babylon_material.metallic_factor;
    }
    set metallic(value) {
        this.babylon_material.metallic_factor = value;
    }
    get roughness() {
        return this.babylon_material.roughness_factor;
    }
    set roughness(value) {
        this.babylon_material.roughness_factor = value;
    }
    get opacity() {
        return this.babylon_material.alpha_factor;
    }
    set opacity(value) {
        this.babylon_material.alpha_factor = value;
    }
    get emissive_factor() {
        return this._emissive_factor;
    }
    set emissive_factor(factor) {
        IVector3.copy_to_ref(factor, this._emissive_factor);
    }
    get emissive_texture() {
        return this.get_texture_id(this.babylon_material.emissive_texture);
    }
    set emissive_texture(texture) {
        if (texture === null) {
            this.babylon_material.emissive_texture = null;
        }
        else {
            this.babylon_material.emissive_texture = this._texture_manager.get(texture);
        }
    }
    get emissive_strength() {
        return this.babylon_material.emissive_strength;
    }
    set emissive_strength(value) {
        this.babylon_material.emissive_strength = value;
    }
}
//# sourceMappingURL=BetraktaPBRMaterial.js.map